//@flow
import {
    outletLocation as outletLocationApi,
    bulkOutletLocationFetch as bulkOutletLocationFetchApi,
    outletLocationBulkGeocode,
    outletLocationBulkGeocodeVerify,
    disableOutletLocation,
    bulkDisableOutletLocation,
    outletLocationGeocodeEdit as outletLocationGeocodeEditApi,
    validateEntityIds as validateEntityIdsApi
} from 'common/middleware/api';

import { showToast } from 'common/redux/actions/toast';
import { ToastTypes } from 'common/components/core/toast/Toast';

import PaginatedEntityActions from 'common/redux/actions/PaginatedEntityActions';
import I18nHelper from 'common/helpers/I18nHelper';

import type { OutletLocation } from 'common/models/internal/OutletLocation';
import { LocusApiModel } from 'common/modules/api';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

type Params = {
    clientId: string,
    locationId?: string,
    overwrite?: boolean
};

class OutletLocationActions extends PaginatedEntityActions<
    OutletLocation,
    Params
> {
    geocodeApi: LocusApiModel;
    entityNameId = 'outletLocation';

    constructor(
        TAG: string,
        api: LocusApiModel,
        bulkFetchApi: LocusApiModel,
        disableApi: LocusApiModel,
        bulkDisableApi: LocusApiModel,
        geocodeApi: LocusApiModel,
        geocodeVerifyApi: LocusApiModel
    ) {
        super(TAG, api, bulkFetchApi, disableApi, bulkDisableApi, geocodeApi);
        this.geocodeApi = geocodeApi;
        this.geocodeVerifyApi = geocodeVerifyApi;
    }

    static EDIT_OUTLET_LOCATION_LATLNG_START =
        'EDIT_OUTLET_LOCATION_LATLNG_START';
    static EDIT_OUTLET_LOCATION_LATLNG_SUCCESS =
        'EDIT_OUTLET_LOCATION_LATLNG_SUCCESS';
    static EDIT_OUTLET_LOCATION_LATLNG_ERROR =
        'EDIT_OUTLET_LOCATION_LATLNG_ERROR';
    static VERIFY_OUTLET_LOCATION_START = 'VERIFY_OUTLET_LOCATION_START';
    static VERIFY_OUTLET_LOCATION_SUCCESS = 'VERIFY_OUTLET_LOCATION_SUCCESS';
    static VERIFY_OUTLET_LOCATION_ERROR = 'VERIFY_OUTLET_LOCATION_ERROR';
    static CLEAR_OUTLET_LOCATION_VALIDATE_GEOCODE_DATA =
        'CLEAR_OUTLET_LOCATION_VALIDATE_GEOCODE_DATA';
    static VALIDATE_OUTLET_LOCATION_GEOCODE_START =
        'VALIDATE_OUTLET_LOCATION_GEOCODE_START';
    static VALIDATE_OUTLET_LOCATION_GEOCODE_SUCCESS =
        'VALIDATE_OUTLET_LOCATION_GEOCODE_SUCCESS';
    static VALIDATE_OUTLET_LOCATION_GEOCODE_ERROR =
        'VALIDATE_OUTLET_LOCATION_GEOCODE_ERROR';

    static startEditLatLng = (params, body) => {
        return {
            type: OutletLocationActions.EDIT_OUTLET_LOCATION_LATLNG_START,
            params,
            body
        };
    };
    static editLatLngSuccess = (params, body) => {
        return {
            type: OutletLocationActions.EDIT_OUTLET_LOCATION_LATLNG_SUCCESS,
            params,
            body
        };
    };
    static editLatLngError = (params, body, error) => {
        return {
            type: OutletLocationActions.EDIT_OUTLET_LOCATION_LATLNG_ERROR,
            params,
            body,
            error
        };
    };
    static startVerification = (params, body) => {
        return {
            type: OutletLocationActions.VERIFY_OUTLET_LOCATION_START,
            params,
            body
        };
    };
    static verificationSuccess = (params, body) => {
        return {
            type: OutletLocationActions.VERIFY_OUTLET_LOCATION_SUCCESS,
            params,
            body
        };
    };
    static verificationError = (params, body, error) => {
        return {
            type: OutletLocationActions.VERIFY_OUTLET_LOCATION_ERROR,
            params,
            body,
            error
        };
    };

    static geocodeValidationStart = (params, body) => {
        return {
            type: OutletLocationActions.VALIDATE_OUTLET_LOCATION_GEOCODE_START,
            params,
            body
        };
    };
    static geocodeValidationSuccess = (params, body, response) => {
        return {
            type:
                OutletLocationActions.VALIDATE_OUTLET_LOCATION_GEOCODE_SUCCESS,
            params,
            response,
            body
        };
    };
    static geocodeValidationError = (params, body, error) => {
        return {
            type: OutletLocationActions.VALIDATE_OUTLET_LOCATION_GEOCODE_ERROR,
            params,
            body,
            error
        };
    };

    types = {
        ...this.types,
        BULK_GEOCODE_SUCCESS: 'BULK_GEOCODE_SUCCESS'
    };

    clearGeocodeValidationData = (params, body) => {
        return {
            type:
                OutletLocationActions.CLEAR_OUTLET_LOCATION_VALIDATE_GEOCODE_DATA,
            params,
            body
        };
    };

    editOutletLocationLatLng = (params: { clientId: string }, body) => {
        return (dispatch: Dispatch<*>) => {
            dispatch(OutletLocationActions.startEditLatLng(params, body));
            return new Promise((resolve, reject) => {
                outletLocationGeocodeEditApi
                    .post(params, body)
                    .then(() => {
                        dispatch(
                            OutletLocationActions.editLatLngSuccess(
                                params,
                                body
                            )
                        );
                        const toast = {
                            message: nsTranslate(
                                'locationTable.bulkEditSuccessToast',
                                { count: body.edits.length }
                            ),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve();
                    })
                    .catch(error => {
                        dispatch(
                            OutletLocationActions.editLatLngError(
                                params,
                                body,
                                error
                            )
                        );
                        const toast = {
                            message: error.message,
                            type: ToastTypes.Error
                        };
                        dispatch(showToast(toast, params));
                        reject(error);
                    });
            });
        };
    };

    validateOutletLocationGeocodes = (
        params: { clientId: string },
        locationIds
    ) => {
        return (dispatch: Dispatch<*>) => {
            const body = { locationIds };
            dispatch(
                OutletLocationActions.geocodeValidationStart(params, body)
            );
            return new Promise((resolve, reject) => {
                outletLocationBulkGeocodeVerify
                    .post(params, body)
                    .then(response => {
                        dispatch(
                            OutletLocationActions.geocodeValidationSuccess(
                                params,
                                body,
                                response
                            )
                        );
                        resolve();
                    })
                    .catch(error => {
                        dispatch(
                            OutletLocationActions.geocodeValidationError(
                                params,
                                body,
                                error
                            )
                        );
                        const toast = {
                            message: error.message,
                            type: ToastTypes.Error
                        };
                        dispatch(showToast(toast, params));
                        reject(error);
                    });
            });
        };
    };

    verifyOutletLocation = (params: { clientId: string }, body) => {
        return (dispatch: Dispatch<*>) => {
            dispatch(OutletLocationActions.startVerification(params, body));
            return new Promise((resolve, reject) => {
                outletLocationGeocodeEditApi
                    .post(params, body)
                    .then(() => {
                        dispatch(
                            OutletLocationActions.verificationSuccess(
                                params,
                                body
                            )
                        );
                        const toast = {
                            message: nsTranslate(
                                'locationTable.bulkEditSuccessToast',
                                { count: body.edits.length }
                            ),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve();
                    })
                    .catch(error => {
                        dispatch(
                            OutletLocationActions.verificationError(
                                params,
                                body,
                                error
                            )
                        );
                        const toast = {
                            message: error.message,
                            type: ToastTypes.Error
                        };
                        dispatch(showToast(toast, params));
                        reject(error);
                    });
            });
        };
    };
}

export default new OutletLocationActions(
    'OUTLET_LOCATION',
    outletLocationApi,
    bulkOutletLocationFetchApi,
    disableOutletLocation,
    bulkDisableOutletLocation,
    validateEntityIdsApi,
    outletLocationBulkGeocode,
    outletLocationBulkGeocodeVerify
);
